import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Save,
  User,
  Phone,
  Mail,
  Building,
  CreditCard,
  FileText,
  AlertCircle
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';

const CreditorFormPage = () => {
  const navigate = useNavigate();
  const { creditorId } = useParams();
  const isEditMode = Boolean(creditorId);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'individual',
    phone: '',
    email: '',
    bankAccount: '',
    category: '',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  // Load creditor data if editing
  useEffect(() => {
    if (isEditMode) {
      // Mock data - سيتم استبداله بـ API
      setTimeout(() => {
        const mockCreditor = {
          name: 'شركة المحاماة المتقدمة',
          type: 'company',
          phone: '0501234567',
          email: 'info@advanced-law.com',
          bankAccount: 'SA1234567890123456789012',
          category: 'خدمات قانونية',
          status: 'active'
        };
        setFormData(mockCreditor);
      }, 300);
    }
  }, [isEditMode, creditorId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'الاسم مطلوب';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    } else if (!/^05\d{8}$/.test(formData.phone)) {
      newErrors.phone = 'رقم الهاتف غير صحيح';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'التصنيف مطلوب';
    }

    if (formData.bankAccount && !/^SA\d{22}$/.test(formData.bankAccount)) {
      newErrors.bankAccount = 'رقم الآيبان غير صحيح (يجب أن يبدأ بـ SA ويحتوي على 24 خانة)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    // Mock API call
    setTimeout(() => {
      console.log('Form Data:', formData);
      alert(isEditMode ? 'تم تحديث بيانات الدائن بنجاح' : 'تم إضافة الدائن بنجاح');
      navigate('/admin/finance/creditors');
      setLoading(false);
    }, 1000);
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/finance/creditors')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors"
          >
            <ArrowRight className="w-5 h-5" />
            <span className="font-semibold">العودة للدائنين</span>
          </button>

          <h1 className="text-3xl font-black text-[#1E3A8A] mb-2">
            {isEditMode ? 'تعديل بيانات الدائن' : 'إضافة دائن جديد'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'تحديث معلومات الدائن' : 'أدخل بيانات الدائن الجديد'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#1E3A8A]" />
              البيانات الأساسية
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  اسم الدائن <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    errors.name
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="مثال: شركة المحاماة المتقدمة"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  النوع <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="individual">فرد</option>
                  <option value="company">شركة</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  الحالة <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
                >
                  <option value="active">نشط</option>
                  <option value="suspended">موقوف</option>
                  <option value="frozen">مجمّد</option>
                  <option value="inactive">غير نشط</option>
                </select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#1E3A8A]" />
              معلومات الاتصال
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Phone */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم الهاتف <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    errors.phone
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="0501234567"
                  dir="ltr"
                />
                {errors.phone && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  البريد الإلكتروني <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    errors.email
                      ? 'border-red-300 focus:border-red-500'
                      : 'border-gray-200 focus:border-[#1E3A8A]'
                  }`}
                  placeholder="info@example.com"
                  dir="ltr"
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Financial & Classification Information */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
            <h2 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-[#1E3A8A]" />
              المعلومات المالية والتصنيف
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bank Account */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رقم الحساب البنكي (IBAN)
                </label>
                <div className="relative">
                  <CreditCard className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="bankAccount"
                    value={formData.bankAccount}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.bankAccount
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                    placeholder="SA1234567890123456789012"
                    dir="ltr"
                  />
                </div>
                {errors.bankAccount && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.bankAccount}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  التصنيف <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full pr-10 pl-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.category
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-[#1E3A8A]'
                    }`}
                    placeholder="مثال: خدمات قانونية، استشارات، توثيق..."
                  />
                </div>
                {errors.category && (
                  <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.category}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>جاري الحفظ...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{isEditMode ? 'حفظ التعديلات' : 'إضافة الدائن'}</span>
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/finance/creditors')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-all"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default CreditorFormPage;
